import { useState, useCallback } from "react";
import { useOrgSettings } from "@/lib/org-settings-context";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DetailCard, DetailGrid, DetailItem } from "@/components/detail-page";
import { ActivityTab } from "@/components/activity-tab";
import { LoadWalletDialog } from "@/components/dialogs";
import { EntityDrawer, useDrawerEntity, useDrawerSelect } from "@/components/entity-drawer";
import { cn } from "@/lib/utils";
import {
  Wallet,
  Search,
  TrendingUp,
  Snowflake,
  CreditCard,
  Receipt,
  Plus,
  ExternalLink,
} from "lucide-react";

interface WalletItem {
  id: string;
  customerId: string;
  currency: string;
  balanceCents: number;
  availableBalanceCents: number;
  pendingBalanceCents: number;
  status: string;
  dailyLimitCents: number | null;
  monthlyLimitCents: number | null;
  maxBalanceCents: number | null;
  createdAt: string;
  updatedAt: string;
  customerName: string | null;
  customerEmail: string | null;
  cardCount: number;
}

interface VirtualCard {
  id: string;
  lastFour: string | null;
  status: string;
  cardType: string;
  nickname: string | null;
  dailyLimitCents: number | null;
  monthlyLimitCents: number | null;
  expMonth: number | null;
  expYear: number | null;
  customerName: string | null;
}

interface WalletTransaction {
  id: string;
  walletId: string;
  type: string;
  amountCents: number;
  balanceBefore: number;
  balanceAfter: number;
  description: string | null;
  reference: string | null;
  createdAt: string;
}

function getStatusBadge(status: string) {
  const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    ACTIVE: "default",
    FROZEN: "secondary",
  };
  const colors: Record<string, string> = {
    ACTIVE: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    FROZEN: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  };
  return <Badge variant={variants[status] || "secondary"} className={colors[status]}>{status}</Badge>;
}

function getCardStatusBadge(status: string) {
  const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    ACTIVE: "default",
    FROZEN: "secondary",
    CANCELLED: "destructive",
    EXPIRED: "outline",
  };
  const colors: Record<string, string> = {
    ACTIVE: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    FROZEN: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  };
  return <Badge variant={variants[status] || "secondary"} className={colors[status]}>{status}</Badge>;
}

// ─── Wallet Detail Content (drawer) ──────────────────────────────────────────

function WalletDetailContent({ walletId, onClose }: { walletId: string; onClose: () => void }) {
  const { formatCurrency } = useOrgSettings();
  const [, setLocation] = useLocation();
  const [loadWalletOpen, setLoadWalletOpen] = useState(false);

  // Single composite query replaces 3 separate API calls
  const { data: detail, isLoading } = useQuery<{
    wallet: WalletItem;
    cards: VirtualCard[];
    transactions: WalletTransaction[];
    counts: { cards: number; transactions: number };
  }>({
    queryKey: ["wallet-detail", walletId],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/wallet/${walletId}/detail`);
      if (!response.ok) throw new Error("Failed to fetch wallet details");
      return response.json();
    },
    enabled: !!walletId,
  });

  const wallet = detail?.wallet;
  const cards = detail?.cards;
  const transactions = detail?.transactions;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!wallet) return null;

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Wallet className="h-5 w-5 text-muted-foreground" />
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Wallet</span>
        </div>
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h2 className="text-xl font-semibold">{wallet.customerName || `Wallet ${wallet.id.slice(0, 8)}`}</h2>
            {getStatusBadge(wallet.status)}
          </div>
          <p className="text-sm text-muted-foreground">{wallet.currency} Wallet</p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" onClick={() => setLoadWalletOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Load Funds
          </Button>
        </div>

        <div className="border-t" />

        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="w-full justify-start">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="cards">Cards ({cards?.length || 0})</TabsTrigger>
            <TabsTrigger value="transactions">Transactions ({transactions?.length || 0})</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <DetailCard title="Balance">
              <div className="flex items-center gap-4 p-4 bg-muted rounded-lg mb-4">
                <Wallet className="h-8 w-8 text-primary" />
                <div>
                  <p className="text-sm text-muted-foreground">Available Balance</p>
                  <p className="text-3xl font-bold">{formatCurrency(wallet.availableBalanceCents)}</p>
                </div>
                {getStatusBadge(wallet.status)}
              </div>
              <DetailGrid columns={1}>
                <DetailItem label="Total Balance" value={formatCurrency(wallet.balanceCents)} />
                <DetailItem label="Available" value={formatCurrency(wallet.availableBalanceCents)} />
                <DetailItem label="Pending" value={formatCurrency(wallet.pendingBalanceCents)} />
              </DetailGrid>
            </DetailCard>

            <DetailCard title="Spending Limits">
              <DetailGrid columns={1}>
                <DetailItem label="Daily Limit" value={formatCurrency(wallet.dailyLimitCents)} />
                <DetailItem label="Monthly Limit" value={formatCurrency(wallet.monthlyLimitCents)} />
                <DetailItem label="Max Balance" value={formatCurrency(wallet.maxBalanceCents)} />
              </DetailGrid>
            </DetailCard>

            <DetailCard title="Wallet Information">
              <DetailGrid columns={1}>
                <DetailItem label="Wallet ID" value={<code className="text-xs bg-muted px-2 py-1 rounded break-all">{wallet.id}</code>} />
                <DetailItem label="Currency" value={<Badge variant="outline">{wallet.currency}</Badge>} />
                <DetailItem
                  label="Customer"
                  value={
                    <Button
                      variant="link"
                      className="p-0 h-auto"
                      onClick={() => setLocation(`/customers?select=${wallet.customerId}`)}
                    >
                      {wallet.customerName || wallet.customerId}
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </Button>
                  }
                />
                <DetailItem label="Customer Email" value={wallet.customerEmail || "-"} />
              </DetailGrid>
            </DetailCard>

            <DetailCard title="Record Information">
              <DetailGrid columns={1}>
                <DetailItem label="Created" value={new Date(wallet.createdAt).toLocaleString()} />
                <DetailItem label="Last Updated" value={new Date(wallet.updatedAt).toLocaleString()} />
              </DetailGrid>
            </DetailCard>
          </TabsContent>

          <TabsContent value="cards">
            <DetailCard title="Virtual Cards" description="Cards linked to this wallet">
              {cards && cards.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Card</TableHead>
                        <TableHead>Nickname</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Expiry</TableHead>
                        <TableHead>Daily Limit</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cards.map((card) => (
                        <TableRow
                          key={card.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => setLocation(`/cards?select=${card.id}`)}
                        >
                          <TableCell className="font-mono">
                            <div className="flex items-center gap-2">
                              <CreditCard className="h-4 w-4 text-muted-foreground" />
                              **** {card.lastFour || "****"}
                            </div>
                          </TableCell>
                          <TableCell>{card.nickname || "-"}</TableCell>
                          <TableCell><Badge variant="outline">{card.cardType}</Badge></TableCell>
                          <TableCell>
                            {card.expMonth && card.expYear ? `${card.expMonth}/${card.expYear}` : "-"}
                          </TableCell>
                          <TableCell>{formatCurrency(card.dailyLimitCents)}</TableCell>
                          <TableCell>{getCardStatusBadge(card.status)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <CreditCard className="mx-auto h-12 w-12 text-muted-foreground" />
                  <p className="mt-2 text-muted-foreground">No cards linked to this wallet</p>
                </div>
              )}
            </DetailCard>
          </TabsContent>

          <TabsContent value="transactions">
            <DetailCard title="Wallet Transactions" description="Fund loads, debits, and transfers">
              {transactions && transactions.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">Balance After</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions.slice(0, 50).map((txn) => (
                        <TableRow key={txn.id}>
                          <TableCell>{new Date(txn.createdAt).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <Badge variant={txn.type === "CREDIT" ? "default" : "secondary"}>
                              {txn.type}
                            </Badge>
                          </TableCell>
                          <TableCell>{txn.description || "-"}</TableCell>
                          <TableCell className={`text-right font-mono ${txn.type === "CREDIT" ? "text-green-600" : "text-red-600"}`}>
                            {txn.type === "CREDIT" ? "+" : "-"}{formatCurrency(txn.amountCents)}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(txn.balanceAfter)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Receipt className="mx-auto h-12 w-12 text-muted-foreground" />
                  <p className="mt-2 text-muted-foreground">No transactions yet</p>
                </div>
              )}
            </DetailCard>
          </TabsContent>

          <TabsContent value="activity">
            <ActivityTab
              entityType="WALLET"
              entityId={wallet.id}
              createdAt={wallet.createdAt}
              updatedAt={wallet.updatedAt}
            />
          </TabsContent>
        </Tabs>
      </div>

      <LoadWalletDialog
        open={loadWalletOpen}
        onOpenChange={setLoadWalletOpen}
        customerId={wallet.customerId}
        customerName={wallet.customerName || "Customer"}
      />
    </>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function WalletView() {
  const { formatCurrency } = useOrgSettings();
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const [searchQuery, setSearchQuery] = useState("");
  const [showFrozen, setShowFrozen] = useState(false);
  const { drawerEntity, setDrawerEntity, displayEntity } = useDrawerEntity<string>();

  // Support ?select= for cross-entity navigation
  useDrawerSelect(setDrawerEntity);

  const { data: wallets, isLoading } = useQuery<WalletItem[]>({
    queryKey: ["wallets"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/wallets");
      if (!response.ok) throw new Error("Failed to fetch wallets");
      return response.json();
    },
  });

  const handleRowClick = useCallback((walletId: string) => {
    setDrawerEntity(prev => prev === walletId ? null : walletId);
  }, [setDrawerEntity]);

  const filteredWallets = wallets?.filter((w) => {
    if (!showFrozen && w.status !== "ACTIVE") return false;
    const matchesSearch =
      w.customerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      w.customerEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      w.id.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const metrics = {
    total: wallets?.length || 0,
    active: wallets?.filter((w) => w.status === "ACTIVE").length || 0,
    frozen: wallets?.filter((w) => w.status === "FROZEN").length || 0,
    totalBalance: wallets?.reduce((sum, w) => sum + w.balanceCents, 0) || 0,
  };

  const metricCards = [
    {
      title: "Total Wallets",
      value: metrics.total,
      icon: Wallet,
      color: "text-primary",
      bgColor: "bg-primary/10",
    },
    {
      title: "Active",
      value: metrics.active,
      icon: TrendingUp,
      color: "text-green-600",
      bgColor: "bg-green-100 dark:bg-green-900/30",
    },
    {
      title: "Frozen",
      value: metrics.frozen,
      icon: Snowflake,
      color: "text-blue-600",
      bgColor: "bg-blue-100 dark:bg-blue-900/30",
    },
    {
      title: "Total Balance",
      value: formatCurrency(metrics.totalBalance),
      icon: CreditCard,
      color: "text-purple-600",
      bgColor: "bg-purple-100 dark:bg-purple-900/30",
      isString: true,
    },
  ];

  return (
    <div className="relative min-h-screen">
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-display font-bold tracking-tight">Wallets</h1>
            <p className="text-muted-foreground">Manage customer wallets and balances</p>
          </div>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {metricCards.map((metric) => (
            <Card key={metric.title}>
              <CardContent className="flex items-center gap-4 p-4">
                <div className={cn("rounded-lg p-2.5", metric.bgColor)}>
                  <metric.icon className={cn("h-5 w-5", metric.color)} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{metric.title}</p>
                  <p className="text-2xl font-bold">
                    {isLoading ? <Skeleton className="h-7 w-12" /> : metric.isString ? metric.value : metric.value}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>All Wallets</CardTitle>
                <CardDescription>
                  {filteredWallets?.length ?? 0} wallet{(filteredWallets?.length ?? 0) !== 1 ? "s" : ""} found
                </CardDescription>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="show-frozen"
                    checked={showFrozen}
                    onCheckedChange={setShowFrozen}
                  />
                  <Label htmlFor="show-frozen" className="text-sm text-muted-foreground whitespace-nowrap">
                    Show Frozen
                  </Label>
                </div>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by customer..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : filteredWallets?.length === 0 ? (
              <div className="text-center py-12">
                <Wallet className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-lg font-semibold">No wallets found</h3>
                <p className="text-muted-foreground">
                  Wallets are created automatically when customers are issued cards.
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Balance</TableHead>
                    <TableHead>Currency</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Cards</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredWallets?.map((wallet) => (
                    <TableRow
                      key={wallet.id}
                      className={cn(
                        "cursor-pointer hover:bg-muted/50",
                        drawerEntity === wallet.id && "bg-primary/10 hover:bg-primary/15"
                      )}
                      onClick={() => handleRowClick(wallet.id)}
                    >
                      <TableCell>
                        <div>
                          <div className="font-medium">{wallet.customerName || "Unknown"}</div>
                          <div className="text-xs text-muted-foreground">{wallet.customerEmail || "-"}</div>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono font-medium">
                        {formatCurrency(wallet.balanceCents)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{wallet.currency}</Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(wallet.status)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{wallet.cardCount}</Badge>
                      </TableCell>
                      <TableCell>{new Date(wallet.createdAt).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Right-side detail drawer */}
      <EntityDrawer isOpen={!!drawerEntity} onClose={() => setDrawerEntity(null)}>
        {displayEntity && (
          <WalletDetailContent key={displayEntity} walletId={displayEntity} onClose={() => setDrawerEntity(null)} />
        )}
      </EntityDrawer>
    </div>
  );
}
